import React, { useState } from 'react';
import { StorageService } from '../services/storage';
import { Check, Shield } from 'lucide-react';

export const PricingView: React.FC = () => {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('annual');
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const user = StorageService.getUser();

  const handleSelectPlan = (plan: 'Free' | 'Pro' | 'Business') => {
    StorageService.setUser({
      id: user?.id || 'u_guest',
      name: user?.name || 'Pro User',
      email: user?.email || 'user@example.com',
      plan: plan
    });
    setToastMessage(`Your account has been switched to the ${plan} plan!`);
    setTimeout(() => setToastMessage(null), 3000);
  };

  const plans = [
    {
      name: 'Free',
      badge: 'Starter',
      priceMonthly: 0,
      priceAnnual: 0,
      description: 'Core app rankings and essential market discovery for individuals.',
      features: [
        'Top 100 App Store & Google Play rankings',
        'Basic 7-day ranking history',
        'Up to 3 apps in Watchlist',
        'Standard keyword search',
        'Daily updates'
      ],
      popular: false,
      cta: 'Current Plan'
    },
    {
      name: 'Pro',
      badge: 'Most Popular',
      priceMonthly: 49,
      priceAnnual: 39,
      description: 'Full growth scores, outlier detection, and competitor intelligence for teams.',
      features: [
        'Complete 0-100 Growth Score breakdown',
        'Proprietary App Outliers Radar & anomaly alerts',
        'Full historical rankings (90D, 1Y, All-time)',
        'Up to 100 apps in Watchlist & Collections',
        'Estimated download & revenue models',
        'Live AI Market Signals & analysis',
        '5-app comparative benchmark engine',
        'ASO Keyword Opportunity matrix'
      ],
      popular: true,
      cta: 'Upgrade to Pro'
    },
    {
      name: 'Business',
      badge: 'Scale & Funds',
      priceMonthly: 249,
      priceAnnual: 199,
      description: 'Institutional-grade data access, API export, and custom category tracking.',
      features: [
        'Everything in Pro',
        'REST API & Webhook data feed access',
        'Unlimited Watchlists & Custom Decks',
        'Custom Outlier & Growth Spike threshold triggers',
        'Dedicated Slack & Email alert channels',
        'Multi-seat team workspace',
        'Export to CSV, PDF & BI dashboards',
        'Priority account manager'
      ],
      popular: false,
      cta: 'Upgrade to Business'
    }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 space-y-12 animate-fadeIn">
      {/* Toast */}
      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 bg-slate-900 text-white px-4 py-3 rounded-2xl shadow-xl border border-slate-700 text-xs font-bold flex items-center gap-2 animate-fadeIn">
          <Check className="w-4 h-4 text-emerald-400" strokeWidth={2.5} />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Header */}
      <div className="text-center max-w-3xl mx-auto space-y-3">
        <div className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-xs font-bold">
          <Shield className="w-3.5 h-3.5" strokeWidth={1.75} />
          <span>Transparent Intelligence Pricing</span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-black text-slate-950 tracking-tight">
          Supercharge your app market radar
        </h1>
        <p className="text-base text-slate-600">
          Choose the right tier to monitor rankings, spot breakout apps, and outmaneuver competitors.
        </p>

        {/* Billing Toggle */}
        <div className="inline-flex items-center gap-2 bg-slate-100 p-1.5 rounded-2xl text-xs font-bold mt-4">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-4 py-1.5 rounded-xl transition-all ${
              billingCycle === 'monthly' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500'
            }`}
          >
            Monthly Billing
          </button>
          <button
            onClick={() => setBillingCycle('annual')}
            className={`px-4 py-1.5 rounded-xl transition-all flex items-center gap-1.5 ${
              billingCycle === 'annual' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500'
            }`}
          >
            <span>Annual Billing</span>
            <span className="bg-emerald-100 text-emerald-800 text-[10px] px-1.5 py-0.2 rounded-full font-bold">
              Save 20%
            </span>
          </button>
        </div>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
        {plans.map((p) => {
          const price = billingCycle === 'annual' ? p.priceAnnual : p.priceMonthly;
          const isCurrent = user?.plan === p.name;

          return (
            <div
              key={p.name}
              className={`rounded-3xl p-7 flex flex-col justify-between transition-all relative ${
                p.popular
                  ? 'bg-gradient-to-b from-slate-900 via-[#0a1835] to-[#0c2452] text-white shadow-2xl border-2 border-blue-500 md:-translate-y-2'
                  : 'bg-white text-slate-900 border border-slate-200/80 shadow-sm'
              }`}
            >
              {p.popular && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-blue-600 to-sky-400 text-white text-[11px] font-black uppercase tracking-wider px-3.5 py-1 rounded-full shadow-md">
                  {p.badge}
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold tracking-tight">{p.name}</h3>
                  {!p.popular && (
                    <span className="text-[11px] font-semibold bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                      {p.badge}
                    </span>
                  )}
                </div>

                <div className="mb-4">
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl sm:text-5xl font-black tracking-tight">
                      ${price}
                    </span>
                    <span className={`text-xs ${p.popular ? 'text-slate-400' : 'text-slate-500'}`}>
                      / month {billingCycle === 'annual' && price > 0 ? '(billed yearly)' : ''}
                    </span>
                  </div>
                  <p className={`text-xs mt-3 leading-relaxed ${p.popular ? 'text-slate-300' : 'text-slate-600'}`}>
                    {p.description}
                  </p>
                </div>

                <div className={`pt-6 border-t ${p.popular ? 'border-white/10' : 'border-slate-100'} space-y-2.5 mb-8 text-xs`}>
                  <span className={`text-[10px] font-bold uppercase tracking-wider block ${p.popular ? 'text-sky-300' : 'text-slate-400'}`}>
                    What is included:
                  </span>
                  {p.features.map((feat) => (
                    <div key={feat} className="flex items-start gap-2.5">
                      <Check className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${p.popular ? 'text-sky-400' : 'text-blue-600'}`} strokeWidth={2.5} />
                      <span className={p.popular ? 'text-slate-200' : 'text-slate-700'}>{feat}</span>
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={() => handleSelectPlan(p.name as any)}
                className={`w-full py-3 rounded-2xl text-xs font-bold transition-all ${
                  isCurrent
                    ? 'bg-emerald-500 text-white cursor-default'
                    : p.popular
                    ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-500/30'
                    : 'bg-slate-900 hover:bg-slate-800 text-white'
                }`}
              >
                {isCurrent ? (
                  <span className="inline-flex items-center justify-center gap-1.5">
                    <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
                    <span>Active Plan</span>
                  </span>
                ) : (
                  p.cta
                )}
              </button>
            </div>
          );
        })}
      </div>

      {/* FAQ Section */}
      <div className="max-w-3xl mx-auto pt-12 border-t border-slate-200 space-y-6">
        <h3 className="text-2xl font-bold text-slate-900 text-center">Frequently Asked Questions</h3>
        <div className="space-y-4 text-xs sm:text-sm">
          <div className="bg-white p-5 rounded-2xl border border-slate-200/80">
            <h4 className="font-bold text-slate-900 mb-1">How is the Growth Score calculated?</h4>
            <p className="text-slate-600 leading-relaxed">
              Our 0–100 proprietary Growth Score weighs ranking velocity (30%), category momentum (25%), search visibility (20%), review sentiment (15%), and update cadence (10%).
            </p>
          </div>
          <div className="bg-white p-5 rounded-2xl border border-slate-200/80">
            <h4 className="font-bold text-slate-900 mb-1">Are the download and revenue figures official?</h4>
            <p className="text-slate-600 leading-relaxed">
              Public rankings and review data are official store signals. Download and revenue metrics are statistical estimation models benchmarked against public financial releases and category baselines.
            </p>
          </div>
          <div className="bg-white p-5 rounded-2xl border border-slate-200/80">
            <h4 className="font-bold text-slate-900 mb-1">Can I cancel anytime?</h4>
            <p className="text-slate-600 leading-relaxed">
              Yes, you can upgrade, downgrade or cancel your subscription at any time with one click from your account dashboard.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
